import { fromPromise, setup, assign } from 'xstate';
import { getUserProfile } from '@leagueplatform/user-profile-api';
import { createActorContext } from '@xstate/react';
import { LOCAL_STORAGE_KEYS } from 'common/constants/constants';
import { localStorageItemExists } from 'common/utils/local-storage-item-exists.util';
import { normalizePhoneNumbers } from 'common/utils/normalize-custom-fields.util';
import type { SnakeCasedScanData } from 'hooks/use-get-scan-user-profile/types';
import { socketAsFetch } from 'common/utils/socket-as-fetch';
import { MESSAGE_TYPES } from 'common/constants/api';

type OnboardingMachineContextType = {
  onboardingCompleted: boolean;
  featureHighlightCompleted: boolean;
  email: string;
  userId: string;
  alternatePhoneNumber: string;
  homePhoneNumber: string;
  mobilePhoneNumber: string;
  isTextNotificationsEnabled: boolean;
  userFullName: string;
};

export const onboardingMachine = setup({
  types: {
    context: {} as OnboardingMachineContextType,
  },
  actors: {
    fetchUserProfileData: fromPromise(async () => {
      const data = await getUserProfile();
      const scanData = data as unknown as SnakeCasedScanData;

      const onboardingCompleted = !!data?.ftux_complete;
      const phoneNumbers = normalizePhoneNumbers(scanData?.phone_numbers);

      return {
        onboardingCompleted,
        email: data?.email || '',
        userId: data.user_id,
        alternatePhoneNumber: phoneNumbers?.alternate?.number || '',
        homePhoneNumber: phoneNumbers?.home?.number || '',
        mobilePhoneNumber: phoneNumbers?.mobile?.number || '',
        isTextNotificationsEnabled:
          !data?.user_profile.notification_preferences.disable_sms,
        userFullName: `${data?.user_profile?.first_name ?? ''} ${
          data?.user_profile?.last_name ?? ''
        }`,
      };
    }),
    setPostFTUX: fromPromise(
      async ({
        input,
      }: {
        input: Pick<OnboardingMachineContextType, 'userId'>;
      }) => {
        const { userId } = input;
        try {
          await socketAsFetch({
            message_type: MESSAGE_TYPES.SET_POST_FTUX,
            info: {
              onboarding_complete: true,
              user_id: userId,
            },
          });
        } catch (e) {
          throw new Error(
            `Scan Socket Fetch Response Error - ${MESSAGE_TYPES.SET_POST_FTUX}`,
          );
        }

        return {};
      },
    ),
  },
  actions: {
    setLocalStorageValue: () => {
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.FEATURE_HIGHLIGHTS_COMPLETED,
        'true',
      );
    },
  },
  guards: {
    userCompletedOnboarding: ({ event }) => !!event.output.onboardingCompleted,
    featureHighlightsCompleted: () =>
      localStorageItemExists(LOCAL_STORAGE_KEYS.FEATURE_HIGHLIGHTS_COMPLETED),
    promptTextNotificationOptIn: ({ context }) => {
      const { mobilePhoneNumber, isTextNotificationsEnabled } = context;
      /*
       * Display the text notification opt-in view if:
       * - The user has not opted into text notifications
       * - The user has opted into text notifications but has not provided a mobile number
       */
      return (
        isTextNotificationsEnabled === false ||
        (isTextNotificationsEnabled === true && mobilePhoneNumber === '')
      );
    },
  },
}).createMachine({
  id: 'onboarding',
  initial: 'initializing',
  context: {
    onboardingCompleted: false,
    featureHighlightCompleted: false,
    email: '',
    userId: '',
    alternatePhoneNumber: '',
    homePhoneNumber: '',
    mobilePhoneNumber: '',
    isTextNotificationsEnabled: false,
    userFullName: '',
  },
  states: {
    initializing: {
      id: 'initializing',
      initial: 'fetchUserProfileData',
      states: {
        fetchUserProfileData: {
          id: 'fetchUserProfileData',
          invoke: {
            id: 'fetchUserProfileData',
            src: 'fetchUserProfileData',
            onDone: [
              {
                guard: 'userCompletedOnboarding',
                target: '#onboardingComplete',
                actions: [
                  assign(({ event }) => event.output),
                  { type: 'setLocalStorageValue' },
                ],
              },
              {
                guard: 'featureHighlightsCompleted',
                target: '#accountSetup',
                actions: assign(({ event }) => event.output),
              },
              {
                target: '#featureHighlights',
                actions: assign(({ event }) => event.output),
              },
            ],
          },
        },
      },
    },
    featureHighlights: {
      id: 'featureHighlights',
      on: {
        featureHighlightsCompleted: {
          target: '#accountSetup',
          actions: [
            { type: 'setLocalStorageValue' },
            assign({ featureHighlightCompleted: true }),
          ],
        },
      },
    },
    accountSetup: {
      id: 'accountSetup',
      initial: 'accountSetup',
      states: {
        accountSetup: {
          id: 'accountSetup',
          on: {
            navigateToNextView: [
              {
                guard: 'promptTextNotificationOptIn',
                target: 'textNotificationOptIn',
                actions: assign(({ event }) => ({
                  ...event.payload,
                })),
              },
              {
                target: 'setPostFTUX',
              },
            ],
          },
        },
        textNotificationOptIn: {
          id: 'textNotificationOptIn',
          on: {
            navigateToAccountSetupCompleted: {
              target: 'setPostFTUX',
            },
          },
        },
        setPostFTUX: {
          id: 'setPostFTUX',
          invoke: {
            src: 'setPostFTUX',
            input: ({ context: { userId } }) => ({ userId }),
            onDone: [
              {
                target: 'accountSetupCompleted',
              },
            ],
            onError: [
              {
                target: '#error',
              },
            ],
          },
        },
        accountSetupCompleted: {
          on: {
            userSelectedContinue: {
              target: '#onboardingComplete',
            },
          },
        },
      },
    },
    onboardingComplete: {
      id: 'onboardingComplete',
      type: 'final',
    },
    error: {
      id: 'error',
      type: 'final',
    },
  },
});

export const OnboardingMachineContext = createActorContext(onboardingMachine);
